// @ts-check
import { createLog } from './logger'
import { getMolGvlVersion, getFallbackMolGvlVersion } from './gvlVersion'
const log = createLog()
/**
 * @param {Array<{key: keyof FetchVendorListsResponse, url: string, versionFn: (json: VendorList) => number|string}>} urls
 * @returns {FetchVendorListResponse}
 */
async function fetchVendor(urls) {
  const key = urls[0].key
  try {
    for (const { key, url, versionFn } of urls.filter((u) => u)) {
      const response = await fetch(url)
      if (response.status === 404) {
        log('unable to find', url)
        continue
      } else {
        return [
          key,
          await response.json().then((json) => ({
            version: versionFn(json),
            json
          }))
        ]
      }
    }
  } catch (e) {
    console.error('error fetch vendor-list:', e.message)
    return [key, 'error']
  }
  return [key, 'error']
}

const gvlUrlBase = '//cmp.dmgmediaprivacy.co.uk/gvl'
const fileName = 'vendor-list.json'

/**
 * @param {keyof FetchVendorListsResponse} key
 * @param {number|string} gvlVersion
 * @returns {Promise<[keyof FetchVendorListsResponse, FetchResponse|GVLFetchResponse]>}
 */
export async function fetchGvl(key, gvlVersion) {
  const versionParts = /** @type {import('./gvlVersion').GVLVersion} */ (
    gvlVersion.toString().split('/')
  )

  /**
   * @type {Array<{key: keyof FetchVendorListsResponse, url: string, versionFn: (json: VendorList) => string}>}
   */
  const urls = [
    {
      key,
      url: `${gvlUrlBase}/${gvlVersion}/${fileName}`,
      versionFn: (json) => {
        return getMolGvlVersion(versionParts, json.vendorListVersion)
      }
    }
  ]

  const fallback = getFallbackMolGvlVersion(versionParts)

  if (fallback) {
    const [fallbackVersion, fallbackVersionParts] = fallback
    urls.push({
      key,
      url: `${gvlUrlBase}/${fallbackVersion}/${fileName}`,
      versionFn: (json) => getMolGvlVersion(fallbackVersionParts, json.vendorListVersion)
    })
  }
  return fetchVendor(urls)
}

/**
 * @param {FetchVendorListOpts} opts
 * @returns {Promise<FetchVendorListsResponse>}
 */
export async function fetchVendorLists({
  gvlVersion,
  cvlVersion,
  nrvVersion,
  migratedGvlVersion
}) {
  const cvlUrl = `//cmp.dmgmediaprivacy.co.uk/custom/${cvlVersion}/vendor-list.json`
  const nrvUrl = `//cmp.dmgmediaprivacy.co.uk/nrv/${nrvVersion}/vendor-list.json`
  /**
   * @type {Array<Promise<[keyof FetchVendorListsResponse, FetchResponse|GVLFetchResponse]>>}
   */
  const vendorLists = [
    fetchGvl('gvl', gvlVersion),
    fetchVendor([{ key: 'cvl', url: cvlUrl, versionFn: () => cvlVersion }]),
    fetchVendor([{ key: 'nrv', url: nrvUrl, versionFn: () => nrvVersion }])
  ]

  if (migratedGvlVersion) {
    vendorLists.push(fetchGvl('newGvl', migratedGvlVersion))
  }
  return /** @type FetchVendorListsResponse */ (
    Object.fromEntries(await Promise.all(vendorLists))
  )
}

/**
 * @typedef {Object} FetchVendorListOpts
 * @property {string|number} gvlVersion
 * @property {string|number} cvlVersion
 * @property {string|number} nrvVersion
 * @property {string|undefined} migratedGvlVersion
 */

/**
 * @typedef {Promise<[keyof FetchVendorListsResponse, FetchResponse|GVLFetchResponse]>} FetchVendorListResponse
 */

/**
 * @typedef {VendorListRaw|string} FetchResponse
 */

/**
 * @typedef {GVLRaw|string} GVLFetchResponse
 */

/**
 * @typedef {{json: Record<string, any>, version: number|string}} VendorListRaw
 */

/**
 * @typedef {{json: VendorList, version: number|string}} GVLRaw
 */

/**
 * @typedef {{ gvl: GVLFetchResponse, cvl: FetchResponse, nrv: FetchResponse, newGvl?: GVLFetchResponse }} FetchVendorListsResponse
 */

/**
 * @typedef {import('@mol-ads/tcf-core').VendorList} VendorList
 */
